// src/utils/lastRole.js
// Remembers, on this phone or computer, the role of whoever signed in last, so the
// next launch can start downloading that person's first screen straight away.
//
// Why: the app's code arrives in steps. Home used to start downloading only after
// the dashboard's own code had arrived and run. With the role remembered, App.js
// starts the right page alongside the dashboard. Since 09/27/2026 that is Home for
// everyone: Client Care (role "Supervisor") used to open on Transactions and now
// open on their Dashboard, where My Checklist is.
//
// It is only a head start. The note is one word, holds nothing private, is written
// only once Firebase has confirmed the sign-in (App.js), and is wiped whenever
// nobody is signed in. A wrong, missing or unreadable note never changes what
// anyone sees or can open, only which code starts downloading first, and every
// storage failure falls back to Home, as before.

export const LAST_ROLE_KEY = "tes:lastRole";
// "Someone has signed in on this device before" (see the section further down).
export const SIGNED_IN_BEFORE_KEY = "tes:signedInBefore";

// Some browsers throw the moment localStorage is touched (site data blocked).
function localStore() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readLastRole(storage = localStore()) {
  try {
    return storage?.getItem(LAST_ROLE_KEY) || null;
  } catch {
    return null;
  }
}

export function rememberLastRole(role, storage = localStore()) {
  try {
    if (!storage) return;
    if (typeof role === "string" && role) storage.setItem(LAST_ROLE_KEY, role);
    else storage.removeItem(LAST_ROLE_KEY);
  } catch {
    // Storage full or blocked: the next launch simply falls back to Home.
  }
}

// Wipes both notes on this device: the role, and the signed-in-before note below.
export function forgetLastRole(storage = localStore()) {
  try {
    storage?.removeItem(LAST_ROLE_KEY);
  } catch {
    // Nothing could have been stored there either.
  }
  try {
    storage?.removeItem(SIGNED_IN_BEFORE_KEY);
  } catch {
    // Same.
  }
}

// ---------------------------------------------------------------------------
// The signed-in-before note (2026-09-14, speed wave 5).
//
// Firebase's usual start-up, getAuth(), brings Google's popup sign-in helper, and on
// an iPhone, iPad or Safari it downloads that helper (two extra sites, about 95 KB)
// BEFORE it reports who is signed in, so every launch waited on it. The helper is
// only needed by the login page's Continue With Google button.
//
// So this note says "someone has signed in on this device before". When it is there,
// firebase/firebaseConfig.js starts sign-in without the helper, and the Google button
// hands it over at the tap (login-form/loginForm.jsx). The note is written only once
// Firebase has confirmed a sign-in (App.js), and is wiped with the role whenever
// nobody is signed in. If the app had started without the helper, it then reloads
// once, so the login page starts with the helper ready.
//
// A missing or unreadable note only means the helper downloads as it always did.
// The note's key, SIGNED_IN_BEFORE_KEY, sits beside LAST_ROLE_KEY at the top.

export function readSignedInBefore(storage = localStore()) {
  try {
    return storage?.getItem(SIGNED_IN_BEFORE_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberSignedInBefore(storage = localStore()) {
  try {
    storage?.setItem(SIGNED_IN_BEFORE_KEY, "1");
  } catch {
    // Storage full or blocked: the next launch downloads the helper, as before.
  }
}

// Nobody is signed in any more. Reload only when the app started without the helper
// AND the note is confirmed gone, so the reloaded app starts with the helper and never
// reloads again. A device that cannot read or wipe the note is never reloaded.
export function shouldReloadForGoogleSignIn({ authStartedWithoutPopupHelper, storage = localStore() }) {
  if (!authStartedWithoutPopupHelper || !storage) return false;
  try {
    return storage.getItem(SIGNED_IN_BEFORE_KEY) === null;
  } catch {
    return false;
  }
}
// The reload itself is utils/reloadPage.js.

// Everyone lands on Home (App.js PublicRoute), Client Care included since 09/27/2026.
// Home draws Client Care's own dashboard (pages/home.jsx), a download of its own, so
// for them it starts too; everyone else never needs it.
function landingPagesFor(role) {
  return role === "Supervisor" ? ["home", "supervisorHome"] : ["home"];
}

// A failed early download is ignored: the page downloads again when it is opened
// (utils/lazyPage.js).
function startQuietly(start) {
  try {
    Promise.resolve(start?.()).catch(() => {});
  } catch {
    // Same: the page downloads when it is opened.
  }
}

// Runs once, when the app's main file loads. `preload` holds a starter for the
// quick reader's code, the dashboard shell, Home and Transactions. Clients
// opening a portal or feedback link, agents opening the agent website and sellers
// opening a decision link never need any of it, and the test runner downloads
// nothing early.
//
// The quick reader goes first (2026-09-16). It reads the signed-in person's own
// record (utils/fastUserRead.js), and it is both the smallest of these downloads
// and the first one needed: sign-in is confirmed before any page can draw.
// Fetched at that moment instead, it would sit on the critical path of the very
// read it exists to speed up. Starting it here reads nothing — it only fetches
// code — so the rule that nothing is read before sign-in still holds.
//
// But only where somebody has actually signed in on this device (2026-09-16
// review). Those 28 KB can never be used by a device nobody signs in on — a login
// page on a fresh phone, a shared or kiosk browser, a visitor who opens the site
// and leaves — and they are queued ahead of the pages that WILL be used. The note
// above already records it, for Google's popup helper. A first sign-in here simply
// fetches the quick reader at sign-in, as it did before, and the reader falls back
// to today's on its own if it has not arrived; every returning staff launch, the
// case this speed work is about, still gets the head start. A note that cannot be
// read costs only that head start, never anything anyone sees.
//
// The login page goes last, on those same devices (09/16/2026, third review round).
// Such a device skips the login page at launch, so its small file (about 12 KB) was
// never fetched, until Sign Out on the Can't Reach screen needed it: with no signal,
// the one moment that screen shows. Fetched now, while the launch still has the
// signal that just brought the app's own files, it is on hand for that sign-out.
// Last, because every other download here is needed sooner.
export function startLaunchDownloads({ env, hash, storage = localStore(), preload }) {
  if (env === "test") return;
  if (/^#\/(portal|feedback|host|openhouses|oh-decide|new-address|referral-sign|appt-confirm)(\?|\/|$)/.test(hash || "")) return;
  const signedInHere = readSignedInBefore(storage);
  if (signedInHere) startQuietly(preload.fastLane);
  startQuietly(preload.dashboard);
  landingPagesFor(readLastRole(storage)).forEach((page) => startQuietly(preload[page]));
  if (signedInHere) startQuietly(preload.login);
}

// The dashboard shell used to start Home's download for everyone as soon as its
// own code ran. With a remembered role App.js has already started the right page,
// so the shell does it only when there is none (the first launch on a device, or
// after signing out).
export function shellShouldPreloadHome({ env = process.env.NODE_ENV, storage = localStore() } = {}) {
  return env !== "test" && !readLastRole(storage);
}
